```vue
<template>
  <van-popup
    v-model:show="visible"
    position="center"
    :overlay="true"
    :closeable="true"
    :close-on-click-overlay="false"
    round
    :style="{
      width: '85%',
      maxWidth: '560px',
      background: 'linear-gradient(180deg, #D8E0FF 0%, #FFFFFF 100%)',
      borderRadius: '24px'
    }"
  >
    <div class="version-update-dialog">
      <!-- 用户账号信息 -->
      <div>
        <div class="version-title">Your account</div>

        <div class="p-10px bg-#fff rounded-16px mb-10px">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-15px color-#242633">
                {{ info.nickname }}
              </div>

              <div class="text-12px color-#7C7D85 py-5px">
                ID:{{ info.userId }}
              </div>
            </div>

            <div
              class="bg-#F3F3F3 rounded-20px pr-16px pl-5px py-3px flex items-center justify-center"
            >
              <div class="w-16px h-16px flex items-center justify-center">
                <img
                  class="w-full h-full"
                  src="@/assets/images/message/crystal.svg"
                  alt=""
                />
              </div>

              <div class="text-12px color-#242633">
                {{ balance }}
              </div>
            </div>
          </div>

          <!-- 已绑定邮箱 -->
          <div
            v-if="info.email"
            class="text-12px color-#7C7D85"
          >
            <span class="color-#242633">
              Email:
            </span>

            {{ info.email }}
          </div>

          <!-- 未绑定邮箱 -->
          <div
            v-else
            class="text-12px color-#7C7D85 flex items-center justify-between"
          >
            <div class="color-#242633">
              Email:
            </div>

            <div
              class="color-#5A68FF border-1px border-#5A68FF flex items-center justify-center rounded-10px px-5px py-0px bind-email-box"
              @click="handleBindEmail"
            >
              Bind email >
            </div>
          </div>

          <div class="w-100% h-1px bg-#E5E7EB mb-10px mt-5px"></div>

          <!-- 邮箱提示 -->
          <div class="flex items-center justify-center">
            <div
              class="w-20px mr-5px flex-shrink-0 items-center justify-center"
            >
              <img
                v-if="info.email"
                class="w-full h-full"
                src="@/assets/images/other/dui.svg"
                alt=""
              />

              <img
                v-else
                class="w-full h-full"
                src="@/assets/images/other/warning.svg"
                alt=""
              />
            </div>

            <div
              v-if="info.email"
              class="text-12px color-#11AA66 lh-16px mt-5px"
            >
              After updating, please log in using your email account to avoid losing account data.
            </div>

            <div
              v-else
              class="text-12px color-#FF9301 lh-16px mt-5px"
            >
              Please bind your email to ensure the security of your account data.
            </div>
          </div>
        </div>
      </div>

      <!-- 版本更新标题 -->
      <div class="version-title">
        {{ title || t('versionUpdate.title') }}
      </div>

      <!-- 更新内容 -->
      <div class="update-content-card">
        <div class="content-title">
          {{ contentTitle || t('versionUpdate.updateContent') }}
        </div>

        <div class="content-list">
          <div
            v-for="(item, index) in updateContent"
            :key="index"
            class="content-item"
          >
            {{ item }}
          </div>
        </div>

        <!-- 更新按钮 -->
        <div class="update-actions">
          <a
            class="update-link"
            :href="updateUrl"
            @click="handleUpdate"
          >
            {{ updateButtonText || t('versionUpdate.updateNow') }}
          </a>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch
} from 'vue'

import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { showLoadingToast } from '@/utils/toast'

interface Props {
  modelValue?: boolean
  show?: boolean

  nickname?: string
  userId?: string
  email?: string

  title?: string
  contentTitle?: string
  updateContent?: string[]
  updateButtonText?: string

  showLoginTip?: boolean

  /**
   * 更新地址
   */
  updateUrl?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:show', value: boolean): void
  (e: 'update'): void
  (e: 'close'): void
}

const props = withDefaults(
  defineProps<Props>(),
  {
    modelValue: true,
    show: true,

    nickname: 'Nickname',
    userId: '12345678',
    email: 'user@example.com',

    updateContent: () => [
      'Client experience optimization',
      'Fix known BUG issues'
    ],

    updateButtonText: 'Update now',

    showLoginTip: true,

    updateUrl:
      'https://apps.apple.com/us/app/lulu-character-ai-chat/id6769624630'
  }
)

const emit = defineEmits<Emits>()

const router = useRouter()

const { t } = useI18n()

const userStore = useUserStore()

/**
 * 宝石余额
 */
const balance = computed(() => {
  return userStore.getMsgCost()
})

/**
 * 用户信息
 */
const info = computed(() => {
  return userStore.getTouristInfo()
})

/**
 * 弹窗显示状态
 *
 * 同时兼容：
 *
 * v-model
 *
 * v-model:show
 */
const visible = ref<boolean>(
  typeof props.show === 'boolean'
    ? props.show
    : !!props.modelValue
)

/**
 * 外部 modelValue 更新
 */
watch(
  () => props.modelValue,
  (val) => {
    if (val !== undefined) {
      visible.value = val
    }
  }
)

/**
 * 外部 show 更新
 */
watch(
  () => props.show,
  (val) => {
    if (val !== undefined) {
      visible.value = !!val
    }
  }
)

/**
 * 内部弹窗状态变化
 */
watch(
  visible,
  (val) => {
    emit('update:modelValue', val)
    emit('update:show', val)

    if (!val) {
      emit('close')
    }
  }
)

/**
 * 绑定邮箱
 */
const handleBindEmail = () => {
  router.push({
    name: 'Login',

    query: {
      isRegisterUrl: 'true',
      isSignInUrl: 'false',
      isLoginUrl: 'true'
    }
  })

  visible.value = false
}

/**
 * 点击更新
 *
 * 注意：
 *
 * 不调用 preventDefault
 * 不使用 window.location.href
 *
 * 由 <a> 标签负责跳转。
 */
const handleUpdate = () => {
  showLoadingToast(
    t('versionUpdate.updating'),
    {
      duration: 2000
    }
  )

  /**
   * 删除聊天数据库
   */
  indexedDB.deleteDatabase('ChatRecordsDB')

  /**
   * 删除 Roleplay 数据库
   */
  indexedDB.deleteDatabase('RoleplayH5')

  /**
   * 通知父组件
   */
  emit('update')
}
</script>

<style scoped lang="scss">
.version-update-dialog {
  padding: 32px 10px 24px;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 99;
}

.close-icon {
  width: 24px;
  height: 24px;
}

.dialog-title {
  font-weight: 900;
  font-size: 22px;
  font-style: italic;
  color: #01031B;
  margin-bottom: 24px;
  text-align: center;
  line-height: 1.2;
}

.user-info-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 10px 15px;
  margin-bottom: 12px;
}

.user-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.user-label {
  font-weight: 700;
  font-size: 15px;
  color: #01031B;
}

.user-balance {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #F3F4F6;
  padding: 0 16px 0 6px;
  border-radius: 20px;
}

.crystal-icon {
  width: 16px;
  height: 16px;
}

.balance-text {
  font-weight: 600;
  font-size: 16px;
  color: #01031B;
}

.user-id {
  font-weight: 500;
  font-size: 14px;
  color: #9CA3AF;
}

.user-email {
  font-weight: 500;
  font-size: 14px;
  color: #9CA3AF;
  padding-bottom: 8px;
  border-bottom: 1px solid #E5E7EB;
  margin-bottom: 4px;
}

.tip-box {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 12px;
}

.tip-text {
  flex: 1;
  font-size: 12px;
  color: #10B981;
}

.version-title {
  font-weight: 900;
  font-size: 22px;
  font-style: italic;
  color: #01031B;
  margin-bottom: 12px;
  text-align: center;
  line-height: 1.2;
}

.update-content-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
}

.content-title {
  font-weight: 700;
  font-size: 18px;
  color: #01031B;
  margin-bottom: 6px;
}

.content-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.content-item {
  font-weight: 500;
  font-size: 15px;
  color: #6B7280;
  line-height: 1.6;
}

.update-actions {
  width: 100%;
}

/**
 * a 标签模拟原来的 van-button
 */
.update-link {
  margin-top: 40px;

  width: 100%;
  height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  border-radius: 27px;

  background: linear-gradient(
    135deg,
    #667EEA 0%,
    #95A5FF 100%
  );

  font-weight: 700;
  font-size: 17px;

  color: #FFFFFF;

  letter-spacing: 0.5px;

  text-decoration: none;

  -webkit-tap-highlight-color: transparent;

  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.98);
  }
}

:deep(.van-popup__close-icon) {
  color: #999;
  font-size: 22px;
  top: 16px;
  right: 16px;
}

.email-text {
  border: 1px solid #5A68FF;
  border-radius: 10px;
  padding: 0 10px;
}

.bind-email-box {
  border: 1px solid #5A68FF;
  border-radius: 15px;
  font-size: 12px;
}
</style>
```
